import React, { useEffect, useMemo } from 'react';
import { JsonDataBlobComponentFragment } from '@graphql/generated';
import { CustomComponentsProps } from '@website-editor/customComponents/CustomComponents';
import { useGetGroupedJSONComponents } from './useGetJSONComponents';
import { ComponentSectionHeader } from '@website-editor/customComponents/ComponentSectionHeader';
import { useTranslation } from '@shared/core';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { JSON_COMPONENT_ANIMATION_CONFIG, JSON_COMPONENT_SCREENS } from './JsonComponent.constants';
import { getJsonComponentNavigations } from './JsonComponent.navigations';

interface GroupedJsonComponentsRendererProps
  extends CustomComponentsProps,
    Readonly<{
      firstJsonComponent: JsonDataBlobComponentFragment;
      textComponents: ReadonlyArray<JSX.Element>;
      photoVideoGalleryAccordion: JSX.Element | null;
    }> {}

/**
 * Renders grouped JSON components with section headers for AddComponentList mode.
 * This component must be used at the React component level (not in useMemo) to properly call hooks.
 */
export const GroupedJsonComponentsRenderer: React.FC<GroupedJsonComponentsRendererProps> = props => {
  const { firstJsonComponent, textComponents, photoVideoGalleryAccordion, ...restProps } = props;
  const { tq } = useTranslation('websiteEditor');
  const { screens, addNavigations } = useNavigationPaneContext();

  const data = firstJsonComponent.containerContent;
  const groupedComponents = useGetGroupedJSONComponents(
    {
      ...restProps,
      componentData: firstJsonComponent,
    },
    data,
  );

  // Register navigation screens for JSON components
  const jsonComponentNavigations = getJsonComponentNavigations(restProps);
  useEffect(() => {
    if (!screens.includes(JSON_COMPONENT_SCREENS[0])) {
      addNavigations(JSON_COMPONENT_SCREENS, JSON_COMPONENT_ANIMATION_CONFIG, jsonComponentNavigations);
    }
  }, [addNavigations, screens, jsonComponentNavigations]);

  const result = useMemo(() => {
    const resultArray: JSX.Element[] = [];

    // Universal Section
    const universalComponents: JSX.Element[] = [];

    // Add text components to Universal
    universalComponents.push(...textComponents);

    // Add media accordion (Photo/Video/Gallery) if it exists
    if (photoVideoGalleryAccordion) {
      universalComponents.push(photoVideoGalleryAccordion);
    }

    // Add Universal section header and components if there are any
    if (universalComponents.length > 0) {
      resultArray.push(
        <ComponentSectionHeader
          key="universal-header"
          title={tq`Universal`}
        />,
      );
      resultArray.push(...universalComponents);
    }

    // Plan Your Trip Section
    if (groupedComponents.planYourTripComponents.length > 0) {
      resultArray.push(
        <ComponentSectionHeader
          key="plan-your-trip-header"
          title={tq`Plan Your Trip`}
        />,
      );
      resultArray.push(...groupedComponents.planYourTripComponents);
    }

    // Event Details Section
    const eventDetailsComponents: JSX.Element[] = [];

    // Add JSON components from Event Details category
    eventDetailsComponents.push(...groupedComponents.eventDetailsComponents);

    // Add other components that don't fit into specific categories
    eventDetailsComponents.push(...groupedComponents.otherComponents);

    // Add Event Details section header and components if there are any
    if (eventDetailsComponents.length > 0) {
      resultArray.push(
        <ComponentSectionHeader
          key="event-details-header"
          title={tq`Event Details`}
        />,
      );
      resultArray.push(...eventDetailsComponents);
    }

    return resultArray;
  }, [textComponents, photoVideoGalleryAccordion, groupedComponents, tq]);

  return <>{result}</>;
};
